import React, { useMemo } from 'react';

import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@/hooks/useNavigate';
import { theme } from '@/theme';
import { stringOfTo } from '@/utils/navigation';

import { usePageContainerContext } from '../../providers/PageContainerContext';

export const PageBreadcrumbs = () => {
    const { t } = useTranslation();
    const { browserNavigate } = useNavigate();

    const { pageBreadcrumbs } = usePageContainerContext();

    const breadcrumbs = useMemo(
        () =>
            pageBreadcrumbs?.flatMap(({ title, link }, index) => {
                const element = (
                    <Typography
                        key={`${title}-${index}`}
                        variant="bodyXs"
                        component={link ? 'a' : 'span'}
                        href={link ? stringOfTo(link) : undefined}
                        onClick={link ? (event: React.MouseEvent) => browserNavigate({ event, to: link }) : undefined}
                        sx={
                            link
                                ? {
                                      cursor: 'pointer',
                                      transition: 'color 250ms ease',
                                      textDecoration: 'none',
                                      color: theme.palette.primary.main,
                                      '&:hover': { color: theme.palette.primary.main },
                                  }
                                : { color: theme.palette.headings.dark }
                        }
                    >
                        {title}
                    </Typography>
                );

                const sep = (
                    <Typography
                        key={`${title}-${index}-sep`}
                        component="span"
                        variant="bodyXs"
                        sx={{ marginInline: '0.4rem', color: theme.palette.headings.dark }}
                    >
                        {'»'}
                    </Typography>
                );

                return index === pageBreadcrumbs.length - 1 ? [element] : [element, sep];
            }),
        [pageBreadcrumbs, browserNavigate],
    );

    return <div aria-label={t('breadcrumbs')}>{breadcrumbs}</div>;
};
